import { useEffect, useState } from 'react'
import { AnimatePresence, MotionConfig, motion } from 'framer-motion'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Button, StateNote, Toasts, WorkflowMark, surfaceVariants } from '@workflow/ui'
import { useSession } from '@/lib/api/hooks'
import { ApiError } from '@/lib/api/client'
import { RETRY } from '@/lib/constants'
import { useLocation } from '@/app/router'
import { AppShell } from '@/shell/AppShell'
import { SignInScreen } from '@/shell/SignInScreen'
import { AccountGateScreen, NotFoundGateScreen, ProfileSetupScreen, SignInRefusedScreen } from '@/shell/GateScreens'
import { LegalPage } from '@/shell/LegalPages'
import { GateFrame } from '@/shell/GateFrame'
import { TermsAcceptanceScreen } from '@/features/account/TermsAcceptanceScreen'

function createClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // No live stream yet; a slow poll is the safety net and the clock.
        staleTime: 5_000,
        refetchInterval: 30_000,
        // A refusal is an answer, not a failure: 401/403/404/409 never ask
        // again. A rate limit asks again once, inside the window the server
        // itself named — never blindly, which would only feed the ceiling.
        retry: (failureCount, error) => {
          if (!(error instanceof ApiError)) return failureCount < RETRY.transient
          if (error.status === 429) {
            return failureCount < RETRY.rateLimited && (error.retryAfterMs ?? Infinity) <= RETRY.maxWaitMs
          }
          if (error.status >= 400 && error.status < 500) return false
          return failureCount < RETRY.transient
        },
        retryDelay: (failureCount, error) => {
          if (error instanceof ApiError && error.status === 429 && error.retryAfterMs !== null) {
            return Math.min(error.retryAfterMs, RETRY.maxWaitMs)
          }
          return Math.min(RETRY.baseDelayMs * 2 ** failureCount, RETRY.maxWaitMs)
        },
      },
    },
  })
}

export function App() {
  const [client] = useState(createClient)
  return (
    // One switch for reduced motion. Components never check it themselves.
    <MotionConfig reducedMotion="user">
      <QueryClientProvider client={client}>
        <AuthGate />
        {/* Above every surface, including floating windows: a refusal that
            happens inside a dialog is still read. */}
        <Toasts />
      </QueryClientProvider>
    </MotionConfig>
  )
}

/**
 * The gate between the browser and the studio.
 *
 * The session probe is public, so it is the first thing asked and the only
 * thing this gate waits on. What renders afterwards is decided entirely by
 * the account's own status: signed out, invited, pending, blocked — or the
 * console itself. The gate is the only place that asks; nothing inside the
 * shell re-checks.
 *
 * Every screen the gate can show is named by a key, and the key is all the
 * handover sees: when the account moves from the door to the waiting room
 * to the console, the same surface language the pages use carries it — the
 * leaving screen dissolving as the arriving one settles beneath.
 */
function AuthGate() {
  const session = useSession()
  // The gate answers the address bar: without this subscription, pushing a
  // new URL (the door's footnote, the banner's policy link) changes the
  // address with nothing re-rendering, and the visitor goes nowhere.
  const { route } = useLocation()
  const [openingFinished, setOpeningFinished] = useState(false)
  const [revealing, setRevealing] = useState(false)
  const [startupComplete, setStartupComplete] = useState(false)

  // A quick session response used to remove the opening screen before its
  // first frame. Give the small mark one readable beat, once per page load.
  useEffect(() => {
    const timer = window.setTimeout(() => setOpeningFinished(true), 700)
    return () => window.clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (!openingFinished || session.isPending) return
    // The destination has mounted under the cover. Let its hidden state paint
    // before changing both transition classes, including in throttled tabs.
    const timer = window.setTimeout(() => setRevealing(true), 40)
    return () => window.clearTimeout(timer)
  }, [openingFinished, session.isPending])

  useEffect(() => {
    if (!revealing) return
    const timer = window.setTimeout(() => setStartupComplete(true), 560)
    return () => window.clearTimeout(timer)
  }, [revealing])

  const gate = (() => {
    if (session.isPending) return null

    if (session.isError) {
      return {
        key: 'unreachable',
        node: (
          <GateFrame>
            <StateNote
              title="The Ops API cannot be reached."
              detail={session.error.message}
              action={
                <Button size="sm" onClick={() => void session.refetch()}>
                  Try again
                </Button>
              }
            />
          </GateFrame>
        ),
      }
    }

    const { account, auth } = session.data
    if (!account) {
      // The internal legal pages read without signing in — before refusal
      // codes, so a signed-out reader never loses them to a stale query.
      if (route.name === 'privacy' || route.name === 'terms' || route.name === 'cookies') {
        return { key: route.name, node: <LegalPage name={route.name} /> }
      }
      // A refused sign-in arrives home as a code in the URL — the callback's
      // word for what happened. It earns its own page in the gate's voice, not
      // a note on the cover.
      const params = new URLSearchParams(window.location.search)
      const refusal = params.get('authError')
      if (refusal) {
        return { key: 'refused', node: <SignInRefusedScreen code={refusal} email={params.get('email')} /> }
      }
      // A deep link to a real page still opens the door — it resumes after
      // sign-in. Only an address that names nothing is answered with the 404.
      if (route.name === 'not-found') {
        return { key: 'not-found', node: <NotFoundGateScreen /> }
      }
      return { key: 'sign-in', node: <SignInScreen auth={auth} /> }
    }
    if (
      account.role !== 'admin' &&
      session.data.terms.required &&
      !['suspended', 'revoked', 'rejected'].includes(account.status)
    ) {
      return {
        key: `terms-${session.data.terms.version}`,
        node: <TermsAcceptanceScreen email={account.email} issuedOn={session.data.terms.issuedOn} />,
      }
    }
    switch (account.status) {
      case 'invited':
        return { key: 'profile-setup', node: <ProfileSetupScreen email={account.email} /> }
      case 'pending':
        return { key: `gate-${account.status}`, node: <AccountGateScreen kind="pending" email={account.email} /> }
      case 'suspended':
      case 'revoked':
      case 'rejected':
        // Sessions are already revoked the moment a status enters this set, so
        // these render for a session that is about to die — the words are the
        // door's courtesy while it closes.
        return { key: `gate-${account.status}`, node: <AccountGateScreen kind={account.status} email={account.email} /> }
      case 'active':
        // Everyone active gets the one shell; it furnishes itself for the
        // role — the studio's destinations, or a player's own.
        return { key: 'console', node: <AppShell /> }
    }
  })()

  // Mount the actual destination behind the opening layer. Both sides move:
  // the room comes into focus as the mark lifts away, instead of replacing
  // one full-screen surface with another at the end of a timer.
  return (
    <div className="grid h-dvh overflow-hidden">
      <AnimatePresence initial={false}>
        {gate ? (
          <motion.div
            key={gate.key}
            style={{ gridArea: '1 / 1' }}
            inert={!revealing}
            className={startupComplete
              ? 'min-h-0 overflow-y-auto'
              : `min-h-0 overflow-y-auto transition-[opacity,filter,transform] duration-[520ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${revealing ? 'scale-100 opacity-100 blur-0' : 'scale-[0.99] opacity-0 blur-[8px]'}`}
            variants={startupComplete ? surfaceVariants : undefined}
            initial={startupComplete ? 'initial' : false}
            animate={startupComplete ? 'animate' : undefined}
            exit={startupComplete ? 'exit' : undefined}
          >
            {gate.node}
          </motion.div>
        ) : null}
      </AnimatePresence>
      {!startupComplete ? (
        <div
          style={{ gridArea: '1 / 1', zIndex: 10 }}
          aria-hidden={revealing}
          className={`transition-[opacity,filter,transform] duration-[520ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${revealing ? 'pointer-events-none scale-[1.015] opacity-0 blur-[6px]' : 'opacity-100'}`}
        >
          <OpeningScreen />
        </div>
      ) : null}
    </div>
  )
}

function OpeningScreen() {
  return (
    <div role="status" aria-label="Opening Workflow" className="flex min-h-dvh items-center justify-center bg-surface">
      <div className="flex flex-col items-center gap-4 text-foreground">
        <WorkflowMark size={38} busy />
        <span className="wf-heading text-xl">Workflow</span>
        <div className="h-0.5 w-20 overflow-hidden rounded-full bg-foreground/10" aria-hidden>
          <motion.div
            className="h-full w-1/2 rounded-full bg-foreground/55"
            animate={{ x: ['-100%', '200%'] }}
            transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>
      </div>
    </div>
  )
}
